// react
import { SyntheticEvent, useContext } from 'react';

// router
import { useSearchParams } from 'react-router-dom';

// types
import { TransactionWithDaEquivalent } from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// headers
import { getTransactionsHeaders } from './TableBasicSoe.headers';

// ui
import { Table, TableHeader, TableProps } from '../../../ui/Table/Table';

// context
import {
	ContextSoeFilters,
	ContextSoeTransactions,
} from '../../../pages/Project/Reports/Soe/Soe.context';

// mui
import { TableCell, TableFooter, TableRow } from '@mui/material';

// theme
import { theme } from '../../../../theme';

// utils
import { formatCurrency } from '../../../../utils/currency';

// props
export interface TableBasicSoeProps extends Omit<TableProps, 'headers'> {
	data: TransactionWithDaEquivalent[];
	headersVisible?: string[];
}

export const TableBasicSoe = ({ data = [], headersVisible, ...props }: TableBasicSoeProps) => {
	// state

	// hooks
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();

	// context
	const { filters } = useContext(ContextSoeFilters);
	const { transactionsUnchecked, setTransactionsUnchecked } = useContext(ContextSoeTransactions);

	// handlers
	const handleChangeCheckBox = (e: SyntheticEvent<Element>, id: number) => {
		const { checked } = e.target as HTMLInputElement;
		let transactionsUncheckedNew = structuredClone(transactionsUnchecked);
		if (checked) {
			transactionsUncheckedNew = transactionsUncheckedNew.filter((txId) => txId !== id);
		} else {
			transactionsUncheckedNew.push(id);
		}
		setTransactionsUnchecked(transactionsUncheckedNew);

		if (transactionsUncheckedNew.length > 0) {
			searchParams.set('id', transactionsUncheckedNew.map((id) => `!${id}`).join(','));
		} else {
			searchParams.delete('id');
		}
		setSearchParams(searchParams);
	};

	// vars
	const headers: TableHeader[] = getTransactionsHeaders({
		filters,
		headersVisible,
		handleChangeCheckBox,
		t,
		transactionsUnchecked,
	});

	const indexTotalDaPaidColumn = headers.findIndex((header) => header.value === 'totalPaidFromDa');

	// calculate the sum of totalPaidFromDa for all transactions that have DA equivalent data
	const totalPaidFromDaSum = data.reduce((sum, transaction) => {
		// Only include transactions that have DA equivalent data (currencyDa is not null)
		if (transaction.amountLoanDaEquivalent !== null && transaction.currencyDa) {
			const amount = transaction.amountLoanDaEquivalent || 0;
			const tax = transaction.taxLoanDaEquivalent || 0;
			return sum + amount + tax;
		}
		return sum;
	}, 0);

	// get currency from first transaction with DA equivalent, fallback to first transaction currency
	const currency = data.find((tx) => tx.currencyDa)?.currencyDa || data[0]?.currency || 'USD';

	return (
		<Table
			{...props}
			borderRadius={2}
			color="blue"
			data={data}
			EmptyProps={{
				hasContent: true,
				hasTitle: true,
			}}
			hasDividers={true}
			hasStripes={true}
			headers={headers}
			sxTd={{
				textWrap: {
					xs: 'nowrap',
				},
			}}>
			{indexTotalDaPaidColumn !== -1 && data.length > 0 && (
				<TableFooter
					sx={{
						borderTop: `1px solid ${theme.palette.divider}`,
						background: theme.palette.grey[100],
					}}>
					<TableRow>
						<TableCell colSpan={indexTotalDaPaidColumn}></TableCell>
						<TableCell
							sx={{ borderTop: `3px double ${theme.palette.brand.grey[500]}`, fontWeight: 600 }}>
							{formatCurrency({ currency, value: totalPaidFromDaSum })}
						</TableCell>
						<TableCell colSpan={headers.length - indexTotalDaPaidColumn - 1}></TableCell>
					</TableRow>
				</TableFooter>
			)}
		</Table>
	);
};
